// PendingCaseModal.tsx
/**
 * Compact review modal for a pending case.
 *
 * Opens from a Pending Cases card and shows just enough to decide on the case — the summary fields
 * plus the full note history — with Accept / Reject in the footer. Rejecting requires a reason, which
 * is written to the case as a restricted note *before* the decline call, since declining hands the case
 * back to the unassigned queue and the leader may lose write access to it immediately after.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Modal, Stack, Group, Text, Badge, Button, Divider, Skeleton, Textarea, Alert, ScrollArea,
} from '@mantine/core';
import {
  IconCheck, IconX, IconUsers, IconCalendarEvent, IconMapPin, IconAlertCircle, IconNotes,
} from '@tabler/icons-react';
import {
  useLeaderPortalService, LeaderCaseView, CareCaseNote,
} from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import { brandBadgeStyle } from '@/utils/color';
import { badgeStyles } from '@/utils/tableStyles';
import { decodeNote, encodeNote } from '@/utils/noteCodec';

/** `yyyyMMddHHmmss` → `yyyy-MM-dd`. */
function formatSDKDate(value: number | string | null | undefined): string {
  const raw = String(value ?? '');
  if (raw.length < 8) return '—';
  return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
}

/** `yyyyMMddHHmmss` → `yyyy-MM-dd HH:mm`. */
function formatSDKDateTime(value: number | string | null | undefined): string {
  const raw = String(value ?? '');
  if (raw.length < 12) return formatSDKDate(value);
  return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)} ${raw.slice(8, 10)}:${raw.slice(10, 12)}`;
}

/** Notes are stored as HTML; the history list only needs a readable one-liner. */
function noteToPlainText(note: string): string {
  const html = decodeNote(note ?? '');
  if (typeof document === 'undefined') return html;
  const el = document.createElement('div');
  el.innerHTML = html;
  return (el.textContent ?? '').trim();
}

function DetailRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <Group gap="sm" wrap="nowrap" align="flex-start">
      <div style={{ color: 'var(--mantine-color-dimmed)', marginTop: 2 }}>{icon}</div>
      <Stack gap={2} style={{ flex: 1 }}>
        <Text size="xs" c="dimmed">{label}</Text>
        <Text size="sm" fw={600}>{value}</Text>
      </Stack>
    </Group>
  );
}

interface PendingCaseModalProps {
  /** The card's row data — rendered immediately so the modal has content before notes land. */
  careCase: LeaderCaseView | null;
  opened: boolean;
  onClose: () => void;
  /** Called after a successful accept/decline so the parent can refresh its list. */
  onActioned: () => void;
  /** Open straight into the reject-reason form (used by the card's Decline button). */
  startInReject?: boolean;
}

export default function PendingCaseModal({
  careCase,
  opened,
  onClose,
  onActioned,
  startInReject = false,
}: PendingCaseModalProps) {
  const leaderPortal = useLeaderPortalService();

  const [notes, setNotes] = useState<CareCaseNote[] | null>(null);
  const [notesError, setNotesError] = useState(false);

  const [rejecting, setRejecting] = useState(false);   // reason form is open
  const [reason, setReason] = useState('');
  const [reasonTouched, setReasonTouched] = useState(false);
  const [busy, setBusy] = useState(false);

  const caseID = careCase?.caseID;

  const fetchNotes = useCallback(async () => {
    if (!caseID) return;
    setNotesError(false);
    try {
      const result = await leaderPortal.caseNotes(caseID);
      setNotes(Array.isArray(result) ? result : []);
    } catch (e) {
      console.error('Error fetching case notes:', e);
      setNotesError(true);
      setNotes([]);
    }
  }, [leaderPortal, caseID]);

  // Reset per-case state each time the modal opens on a different case.
  useEffect(() => {
    if (!opened) return;
    setNotes(null);
    setRejecting(startInReject);
    setReason('');
    setReasonTouched(false);
    fetchNotes();
  }, [opened, startInReject, fetchNotes]);

  const handleAccept = async () => {
    if (!caseID) return;
    setBusy(true);
    try {
      await leaderPortal.acceptCase(caseID);
      toast.success('Case accepted', 'The case has been moved to your active cases.');
      onActioned();
      onClose();
    } catch (e) {
      console.error('Error accepting case:', e);
      toast.error('Error', 'Could not accept the case.');
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    if (!caseID) return;

    const trimmed = reason.trim();
    if (!trimmed) {
      setReasonTouched(true);
      return;
    }

    setBusy(true);
    try {
      // Note first, then decline — after declining, the case leaves this leader's queue.
      // `hideFromLeader` keeps the reason restricted to the care office.
      await leaderPortal.addCaseNote(caseID, {
        caseID,
        note: encodeNote(`<p><strong>Case declined.</strong> Reason: ${trimmed}</p>`),
        hideFromLeader: true,
      });

      await leaderPortal.declineCase(caseID);

      toast.success('Case declined', 'The reason was saved to the case and it has been returned to the queue.');
      onActioned();
      onClose();
    } catch (e) {
      console.error('Error declining case:', e);
      toast.error('Error', 'Could not decline the case. The reason was not saved.');
    } finally {
      setBusy(false);
    }
  };

  const reasonInvalid = reasonTouched && !reason.trim();

  return (
    <Modal
      opened={opened}
      onClose={busy ? () => {} : onClose}
      title={<Text fw={600}>{careCase?.name || (caseID ? `Case ${caseID}` : 'Case')}</Text>}
      size="lg"
      centered
      scrollAreaComponent={ScrollArea.Autosize}
    >
      {!careCase ? null : (
        <Stack gap="md">
          {/* ── Summary ── */}
          <Group gap={8}>
            {careCase.needName && (
              <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(careCase.needColor)}>
                {careCase.needName}
              </Badge>
            )}
            <Badge variant="filled" styles={badgeStyles} style={brandBadgeStyle(careCase.statusColor)}>
              {careCase.statusName ?? '—'}
            </Badge>
            <Badge variant="light" color={careCase.isLead ? 'blue' : 'gray'}>
              {careCase.isLead ? 'Lead' : 'Support'}
            </Badge>
          </Group>

          <Stack gap="sm">
            <DetailRow
              icon={<IconUsers size={18} />}
              label="Counselees"
              value={careCase.counseleeNames || '—'}
            />
            <DetailRow
              icon={<IconMapPin size={18} />}
              label="Opened"
              value={
                <>
                  {formatSDKDate(careCase.caseDateTime)}
                  {typeof careCase.daysSinceLastActivity === 'number' && (
                    <Text span size="xs" c="dimmed">
                      {' '}· {careCase.daysSinceLastActivity}d since last activity
                    </Text>
                  )}
                </>
              }
            />
            <DetailRow
              icon={<IconCalendarEvent size={18} />}
              label="Next session"
              value={careCase.nextSessionDisplay || 'No session scheduled'}
            />
          </Stack>

          <Divider />

          {/* ── Note history ── */}
          <Group gap={6}>
            <IconNotes size={16} opacity={0.6} />
            <Text fw={600} size="sm">Notes</Text>
          </Group>

          {notes === null ? (
            <Stack gap="xs">
              <Skeleton height={12} width="80%" />
              <Skeleton height={12} width="60%" />
            </Stack>
          ) : notesError ? (
            <Text size="sm" c="dimmed">Could not load the note history.</Text>
          ) : notes.length === 0 ? (
            <Text size="sm" c="dimmed">No notes on this case yet.</Text>
          ) : (
            <ScrollArea.Autosize mah={220}>
              <Stack gap="sm">
                {notes.map((n) => (
                  <Stack key={n.noteID} gap={2}>
                    <Text size="xs" c="dimmed">{formatSDKDateTime(n.entryDateTime)}</Text>
                    <Text size="sm">{noteToPlainText(n.note) || '—'}</Text>
                  </Stack>
                ))}
              </Stack>
            </ScrollArea.Autosize>
          )}

          {/* ── Reject reason ── */}
          {rejecting && (
            <>
              <Divider />
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="red"
                variant="light"
                title="Why are you declining this case?"
              >
                <Text size="xs" mb="xs">
                  The reason is saved to the case for the care office. It is not shown to the
                  counselees or to other leaders.
                </Text>
                <Textarea
                  placeholder="e.g. Conflict of interest — I know the counselee personally."
                  value={reason}
                  onChange={(e) => setReason(e.currentTarget.value)}
                  onBlur={() => setReasonTouched(true)}
                  error={reasonInvalid ? 'A reason is required to decline.' : undefined}
                  minRows={3}
                  autosize
                  data-autofocus
                />
              </Alert>
            </>
          )}

          <Divider />

          {/* ── Actions ── */}
          <Group justify="flex-end" gap="sm">
            {rejecting ? (
              <>
                <Button variant="subtle" onClick={() => setRejecting(false)} disabled={busy}>
                  Back
                </Button>
                <Button
                  color="red"
                  leftSection={<IconX size={16} />}
                  onClick={handleReject}
                  loading={busy}
                  disabled={!reason.trim()}
                >
                  Confirm Decline
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="light"
                  color="red"
                  leftSection={<IconX size={16} />}
                  onClick={() => setRejecting(true)}
                  disabled={busy}
                >
                  Reject
                </Button>
                <Button
                  color="green"
                  leftSection={<IconCheck size={16} />}
                  onClick={handleAccept}
                  loading={busy}
                >
                  Accept
                </Button>
              </>
            )}
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
